import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Loader2, MapPin, Goal, ArrowLeftRight, Circle } from "lucide-react";
import { endpoints } from "@/lib/api";
import { TeamLogo, FavStar, fmtTime } from "@/components/football/MatchRow";

const Card = ({ c }) => <span className={`inline-block w-2.5 h-3.5 rounded-[2px] ${c}`} />;
const icon = (t) => (/GOAL/.test(t) ? <Goal className="w-4 h-4 text-emerald-400" /> : t === "YELLOW_CARD" ? <Card c="bg-yellow-400" />
  : /RED/.test(t) ? <Card c="bg-red-500" /> : t === "SUBSTITUTION" ? <ArrowLeftRight className="w-4 h-4 text-sky-400" /> : <Circle className="w-2 h-2 text-slate-500" />);

// East Africa match centre (Live-Score API) — refreshes every 60s only while live and the tab is visible.
export default function EastAfricaMatch() {
  const { key } = useParams();
  const { state } = useLocation();
  const nav = useNavigate();
  const [m, setM] = useState(state?.match || null);
  const [err, setErr] = useState(false);
  useEffect(() => {
    let alive = true;
    endpoints.eaMatch(key).then((r) => alive && setM((p) => ({ ...(p || {}), ...r.data }))).catch(() => alive && setErr(true));
    return () => { alive = false; };
  }, [key]);
  useEffect(() => {
    if (m?.status !== "live") return undefined;
    const t = setInterval(() => { if (document.visibilityState === "visible") endpoints.eaMatch(key).then((r) => setM((p) => ({ ...p, ...r.data }))).catch(() => {}); }, 60000);
    return () => clearInterval(t);
  }, [key, m?.status]);

  if (!m) return err ? <p className="text-center text-slate-400 py-24" data-testid="ea-match-error">Match not available. <Link to="/football/east-africa" className="text-[#EF4444]">Back</Link></p>
    : <div className="flex justify-center py-24"><Loader2 className="w-7 h-7 animate-spin text-[#E11D2A]" /></div>;
  const played = m.status === "live" || m.status === "finished";
  const stats = Object.entries(m.stats || {});
  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="ea-match-page">
      <button onClick={() => (window.history.length > 1 ? nav(-1) : nav("/football"))} className="text-sm text-slate-400 hover:text-white inline-flex items-center gap-1.5 mb-4" data-testid="ea-match-back"><ArrowLeft className="w-4 h-4" /> Back</button>
      <div className="rounded-2xl bg-[#121620] border border-[#273042] p-5 sm:p-7">
        <div className="text-center text-xs text-slate-400 mb-4">{[m.league, m.round && `Round ${m.round}`].filter(Boolean).join(" · ")}</div>
        <div className="flex items-center gap-3">
          <div className="flex-1 flex flex-col items-center gap-2 text-white font-bold text-center"><TeamLogo team={m.home} size="w-14 h-14" />{m.home?.name}</div>
          <div className="text-center shrink-0" data-testid="ea-match-score">
            {played ? <div className={`text-4xl font-extrabold tabular-nums ${m.status === "live" ? "text-[#EF4444]" : "text-white"}`}>{m.homeScore ?? 0} - {m.awayScore ?? 0}</div>
              : <div className="text-2xl font-extrabold text-white">{fmtTime(m.date)}</div>}
            <div className={`text-xs font-bold mt-1 ${m.status === "live" ? "text-[#EF4444]" : "text-slate-400"}`}>{m.status === "live" ? `LIVE ${m.statusLabel || ""}` : m.status === "finished" ? "Full time" : m.statusLabel || (m.date ? new Date(m.date.replace(" ", "T") + "Z").toLocaleDateString() : "")}</div>
            {m.htScore && <div className="text-[11px] text-slate-500 mt-0.5">HT {m.htScore}</div>}
          </div>
          <div className="flex-1 flex flex-col items-center gap-2 text-white font-bold text-center"><TeamLogo team={m.away} size="w-14 h-14" />{m.away?.name}</div>
        </div>
        <div className="flex justify-center mt-3"><FavStar kind="matches" testid="ea-fav-match" item={{ id: m.id, label: `${m.home?.name} v ${m.away?.name}`, date: m.date, name: m.league }} /></div>
        {m.venue && <div className="text-center text-xs text-slate-500 mt-1 flex items-center justify-center gap-1"><MapPin className="w-3 h-3" />{m.venue}</div>}
      </div>

      <h2 className="text-base md:text-lg font-bold text-white mt-7 mb-3">Timeline</h2>
      {m.events?.length ? (
        <div className="rounded-xl bg-[#121620] border border-[#273042] divide-y divide-[#273042]" data-testid="ea-match-events">
          {m.events.map((e, i) => (
            <div key={i} className={`flex items-center gap-3 px-4 py-2.5 text-sm ${e.side === "away" ? "flex-row-reverse text-right" : ""}`}>
              <span className="w-10 shrink-0 text-center text-xs font-bold text-slate-400">{e.minute}'</span>
              <span className="w-5 flex justify-center" aria-hidden="true">{icon(e.type)}</span>
              <span className="min-w-0"><span className="text-white">{e.player || e.label}</span>{e.related && <span className="block text-[11px] text-slate-500">{e.type === "SUBSTITUTION" ? `for ${e.related}` : `assist ${e.related}`}</span>}</span>
            </div>
          ))}
        </div>
      ) : <p className="text-slate-400 text-sm" data-testid="ea-match-no-events">{m.note || "No match events yet."}</p>}

      {stats.length > 0 && (
        <>
          <h2 className="text-base md:text-lg font-bold text-white mt-7 mb-3">Statistics</h2>
          <div className="rounded-xl bg-[#121620] border border-[#273042] divide-y divide-[#273042]" data-testid="ea-match-stats">
            {stats.map(([k, v]) => {
              const [a, b] = String(v).split(":");
              return <div key={k} className="flex items-center px-4 py-2 text-sm"><span className="w-16 text-white font-bold tabular-nums">{a}</span><span className="flex-1 text-center text-slate-400 capitalize">{k.replace(/_/g, " ")}</span><span className="w-16 text-right text-white font-bold tabular-nums">{b ?? ""}</span></div>;
            })}
          </div>
        </>
      )}
    </div>
  );
}
